import { useMemo, useState } from 'react';
import { BOARD_LEGEND, COLOR_META, COLOR_ORDER } from '@shared/game/board';
import type { AgentProvider, AiMatchPassStatus, GameSettings, RoomView } from '@shared/game/types';
import { Avatar } from './Avatar';
import { Brand } from './Brand';
import { Icon, type IconName } from './Icon';
import { Modal } from './Modal';
import { Toggle } from './Toggle';

interface LobbyScreenProps {
  room: RoomView;
  playerId: string;
  busy: boolean;
  onLeave: () => void;
  onStart: () => void;
  onSettings: (patch: Partial<GameSettings>) => void;
  onAddBot: () => void;
  onRemoveBot: (playerId: string) => void;
  aiPassStatus: AiMatchPassStatus | null;
  testPurchaseEnabled: boolean;
  checkoutEnabled: boolean;
  availableAgentProviders: AgentProvider[];
  onActivateAiPass: () => void;
  onBuyAiPass: () => void;
  onAddAgent: (provider: 'openai' | 'deepseek') => void;
  onRemoveAgent: (agentId: string) => void;
  onCopyCode: () => void;
  onCopyLink: () => void;
  onRules: () => void;
  onSettingsModal: () => void;
  soundEnabled: boolean;
  musicEnabled: boolean;
  onToggleSound: () => void;
  onToggleMusic: () => void;
}

export function LobbyScreen({
  room,
  playerId,
  busy,
  onLeave,
  onStart,
  onSettings,
  onAddBot,
  onRemoveBot,
  aiPassStatus,
  testPurchaseEnabled,
  checkoutEnabled,
  availableAgentProviders,
  onActivateAiPass,
  onBuyAiPass,
  onAddAgent,
  onRemoveAgent,
  onCopyCode,
  onCopyLink,
  onRules,
  onSettingsModal,
  soundEnabled,
  musicEnabled,
  onToggleSound,
  onToggleMusic,
}: LobbyScreenProps) {
  const [briefingOpen, setBriefingOpen] = useState(false);
  const isHost = room.hostId === playerId;
  const host = room.members.find((member) => member.id === room.hostId);
  const agentMembers = room.members.filter((member) => member.seat?.kind === 'agent');
  const passActive = aiPassStatus?.status === 'active';
  const openAiAvailable = availableAgentProviders.includes('openai');
  const deepSeekAvailable = availableAgentProviders.includes('deepseek');
  const passStatusLabel = {
    disabled: 'No disponible',
    available: 'Disponible',
    active: 'Activo',
    expired: 'Caducado',
    exhausted: 'Agotado',
  }[aiPassStatus?.status ?? 'disabled'];
  const seatColors = useMemo(() => COLOR_ORDER.slice(0, room.settings.maxPlayers), [room.settings.maxPlayers]);

  const confirmStart = () => {
    setBriefingOpen(false);
    onStart();
  };

  return (
    <div className="app app--lobby">
      <header className="game-topbar">
        <Brand compact onClick={onLeave} />
        <div className="game-topbar__center">
          <span className={`mode-badge mode-badge--${room.settings.mode}`}>
            <Icon name={room.settings.mode === 'adventure' ? 'spark' : 'sword'} size={13} />
            {room.settings.mode === 'adventure' ? 'Aventura' : 'Clásico'}
          </span>
          <button type="button" className="room-code room-code--button" onClick={onCopyCode} title="Copiar código">
            {room.code} <Icon name="copy" size={13} />
          </button>
        </div>
        <div className="game-topbar__actions">
          <button type="button" className="icon-button" onClick={onToggleSound} aria-label={soundEnabled ? 'Silenciar efectos' : 'Activar efectos'}>
            <Icon name={soundEnabled ? 'volume' : 'volume-off'} size={18} />
          </button>
          <button type="button" className={`icon-button ${musicEnabled ? 'is-active' : ''}`} onClick={onToggleMusic} aria-label={musicEnabled ? 'Desactivar música' : 'Activar música'}>
            <Icon name="music" size={18} />
          </button>
          <button type="button" className="icon-button" onClick={onSettingsModal} aria-label="Ajustes">
            <Icon name="settings" size={18} />
          </button>
          <button type="button" className="button button--ghost button--small" onClick={onLeave}>
            <Icon name="logout" size={15} /> Salir
          </button>
        </div>
      </header>

      <main className="lobby">
        <section className="lobby__hero">
          <div>
            <span className="eyebrow">{room.settings.visibility === 'private' ? 'Sala privada' : 'Sala pública'} · {room.members.length}/{room.settings.maxPlayers} jugadores</span>
            <h1>La arena de <em>{host?.name ?? 'tu escuadrón'}</em></h1>
            <p>
              Comparte el enlace, elegid escuadrón y empezad cuando estéis listos. La arena no se abre hasta que el
              anfitrión lance la partida.
            </p>
            <div className="lobby__invite">
              <div>
                <span className="invite-label">Enlace de invitación</span>
                <code>{`${window.location.origin}/?sala=${room.code}`}</code>
              </div>
              <button type="button" className="button button--secondary button--small" onClick={onCopyLink}>
                <Icon name="link" size={15} /> Copiar enlace
              </button>
            </div>
          </div>
          <div className="lobby__seal">
            <span className="lobby__seal-star"><Icon name="portal" size={28} /></span>
            <span>Código</span>
            <strong>{room.code}</strong>
            <button type="button" className="text-link" onClick={onCopyCode}>Copiar código</button>
          </div>
        </section>

        <section className="ai-pass-card card">
          <div className="ai-pass-card__header">
            <div>
              <span className="eyebrow">Pase de partida IA</span>
              <h2>Hasta 2 agentes durante una partida</h2>
            </div>
            <span className={`ai-pass-card__status ai-pass-card__status--${aiPassStatus?.status ?? 'disabled'}`}>
              {passStatusLabel}
            </span>
          </div>
          <p className="ai-pass-card__intro">
            Los bots locales son gratuitos. El Pase de partida IA desbloquea hasta 2 agentes durante esta partida.
            Máximo 12 turnos por agente. El pack cosmético no incluye este pase.
          </p>
          <div className="ai-pass-card__price">
            <strong>{aiPassStatus?.priceLabel ?? '1,99 €'}</strong>
            <span>precio provisional de staging · pago simulado</span>
          </div>
          <div className="ai-pass-card__meta">
            <span><Icon name="bot" size={14} /> Bots locales gratis</span>
            <span><Icon name="users" size={14} /> {aiPassStatus?.maxAgents ?? 2} agentes máximo</span>
            <span><Icon name="dice" size={14} /> {aiPassStatus?.maxTurnsPerAgent ?? 12} turnos por agente</span>
          </div>
          {aiPassStatus?.message && <p className="ai-pass-card__message">{aiPassStatus.message}</p>}
          {!isHost && (
            <p className="ai-pass-card__message">
              <Icon name="lock" size={14} /> Solo el anfitrión puede activar el pase y añadir agentes.
            </p>
          )}
          {isHost && !passActive && aiPassStatus?.status !== 'disabled' && (checkoutEnabled || testPurchaseEnabled) && (
            <button
              type="button"
              className="button button--primary"
              onClick={checkoutEnabled ? onBuyAiPass : onActivateAiPass}
              disabled={busy}
            >
              <Icon name="spark" size={16} />
              {checkoutEnabled ? 'Comprar pase IA (Stripe test)' : 'Activar pase de prueba'} ·{' '}
              {aiPassStatus?.priceLabel ?? '1,99 €'}
            </button>
          )}
          {isHost && passActive && (
            <div className="ai-pass-card__agents">
              <span className="settings-block__label">Añadir agente premium</span>
              <div className="ai-pass-card__actions">
                <button
                  type="button"
                  className="button button--secondary button--small"
                  onClick={() => onAddAgent('openai')}
                  disabled={busy || agentMembers.length >= 2 || !openAiAvailable}
                  title={openAiAvailable ? 'Agente OpenAI' : 'OpenAI no configurado en el servidor'}
                >
                  <Icon name="bot" size={15} /> {openAiAvailable ? 'OpenAI' : 'OpenAI · no configurado'}
                </button>
                <button
                  type="button"
                  className="button button--secondary button--small"
                  onClick={() => onAddAgent('deepseek')}
                  disabled={busy || agentMembers.length >= 2 || !deepSeekAvailable}
                  title={deepSeekAvailable ? 'Agente DeepSeek' : 'DeepSeek no configurado en el servidor'}
                >
                  <Icon name="bot" size={15} /> {deepSeekAvailable ? 'DeepSeek' : 'DeepSeek · no configurado'}
                </button>
                <span className="ai-pass-card__count">{agentMembers.length}/2 agentes</span>
              </div>
              {!openAiAvailable && !deepSeekAvailable && (
                <p className="ai-pass-card__message">
                  No hay proveedores IA configurados en este servidor. Puedes seguir usando bots locales gratis.
                </p>
              )}
              {agentMembers.map((agent) => (
                <div key={agent.id} className="ai-pass-agent-row">
                  <span><Icon name="bot" size={14} /> {agent.name} · {agent.seat?.kind === 'agent' ? agent.seat.provider : 'IA'}</span>
                  <button type="button" className="text-link" onClick={() => onRemoveAgent(agent.id)} disabled={busy}>
                    Quitar
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="lobby__layout">
          <div className="lobby__main">
            <div className="section-heading">
              <div>
                <span className="eyebrow">Unidades enlazadas</span>
                <h2>Muelles</h2>
              </div>
              <span className="muted small">Se asignan colores al empezar</span>
            </div>

            <div className={`seats seats--${room.settings.maxPlayers}`}>
              {seatColors.map((color, index) => {
                const member = room.members[index];
                const isMe = member?.id === playerId;
                const isSeatHost = member?.id === room.hostId;
                return (
                  <div key={color} className={`seat seat--${color} ${member ? 'is-filled' : 'is-empty'}`}>
                    <span className="seat__color">{COLOR_META[color].label}</span>
                    {member ? (
                      <>
                        <div className="seat__avatar">
                          <Avatar avatar={member.avatar} size={78} />
                          {!member.connected && <span className="seat__offline"><Icon name="wifi-off" size={14} /></span>}
                        </div>
                        <div className="seat__name">
                          <strong>{member.name}</strong>
                          <span>
                            {isSeatHost
                              ? 'Anfitrión'
                              : member.seat?.kind === 'agent'
                                ? `Agente · ${member.seat.provider === 'openai' ? 'OpenAI' : 'DeepSeek'}`
                                : member.isBot
                                  ? 'Espíritu bot'
                                  : isMe
                                    ? 'Tú'
                                    : 'Invitado'}
                          </span>
                        </div>
                        <div className="seat__badges">
                          {isSeatHost && <span className="badge"><Icon name="crown" size={12} /> Anfitrión</span>}
                          {member.seat?.kind === 'agent' && (
                            <span className="badge"><Icon name="bot" size={12} /> Agente IA</span>
                          )}
                          {member.isBot && member.seat?.kind !== 'agent' && (
                            <span className="badge"><Icon name="bot" size={12} /> Bot</span>
                          )}
                          {isMe && <span className="badge badge--soft">Tú</span>}
                          {!member.connected && <span className="badge badge--danger">Desconectado</span>}
                        </div>
                        {isHost && member.isBot && (
                          <button
                            type="button"
                            className="seat__remove"
                            onClick={() =>
                              member.seat?.kind === 'agent' ? onRemoveAgent(member.id) : onRemoveBot(member.id)
                            }
                            aria-label={`Quitar a ${member.name}`}
                          >
                            <Icon name="close" size={14} />
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="seat__empty-orb"><Icon name="user" size={28} /></div>
                        <div className="seat__name">
                          <strong>Muelle libre</strong>
                          <span>Esperando unidad</span>
                        </div>
                        {isHost && (
                          <button type="button" className="button button--ghost button--small" onClick={onAddBot} disabled={busy}>
                            <Icon name="plus" size={14} /> Añadir bot
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="lobby__mode-card card">
              <div className="lobby__mode-heading">
                <span className={`mode-orb mode-orb--${room.settings.mode}`}>
                  <Icon name={room.settings.mode === 'adventure' ? 'spark' : 'sword'} size={23} />
                </span>
                <div>
                  <span className="eyebrow">Reglas de la arena</span>
                  <h2>{room.settings.mode === 'adventure' ? 'Aventura' : 'Clásico'}</h2>
                </div>
                <button type="button" className="text-link" onClick={onRules}>Leer reglas completas</button>
              </div>
              <p>
                {room.settings.mode === 'adventure'
                  ? room.settings.effectsEnabled
                    ? 'Los portales, cofres y fisuras están activos. Cada anomalía se explica antes de aplicarse.'
                    : 'Aventura con anomalías desactivadas: reglas clásicas y zonas seguras.'
                  : 'Cuatro unidades, un dado y tiradas exactas. Sin anomalías, con campos estables y bloqueos.'}
              </p>
              {room.settings.mode === 'adventure' && room.settings.effectsEnabled && (
                <div className="mini-legend">
                  {BOARD_LEGEND.filter((entry) => entry.kind === 'shortcut' || entry.kind === 'event' || entry.kind === 'risk').map((entry) => (
                    <span key={entry.kind} className={`mini-legend__item mini-legend__item--${entry.kind}`}>
                      <Icon name={entry.icon as IconName} size={14} /> {entry.title}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="lobby__aside">
            <div className="host-panel card">
              <div className="card__header">
                <span className="eyebrow">Control de la arena</span>
                {!isHost && <span className="badge badge--soft"><Icon name="lock" size={12} /> Solo lectura</span>}
              </div>

              <div className="settings-block">
                <span className="settings-block__label">Modo de juego</span>
                <div className="segmented segmented--wide">
                  <button
                    type="button"
                    className={room.settings.mode === 'classic' ? 'is-active' : ''}
                    disabled={!isHost || busy}
                    onClick={() => onSettings({ mode: 'classic', effectsEnabled: false })}
                  >
                    Clásico
                  </button>
                  <button
                    type="button"
                    className={room.settings.mode === 'adventure' ? 'is-active' : ''}
                    disabled={!isHost || busy}
                    onClick={() => onSettings({ mode: 'adventure' })}
                  >
                    Aventura
                  </button>
                </div>
              </div>

              <Toggle
                checked={room.settings.mode === 'adventure' && room.settings.effectsEnabled}
                onChange={(effectsEnabled) => onSettings({ effectsEnabled })}
                label="Anomalías de Altia"
                description="Portales, cofres y fisuras."
                disabled={!isHost || busy || room.settings.mode === 'classic'}
              />

              <div className="settings-block">
                <span className="settings-block__label">Plazas</span>
                <div className="segmented" role="group" aria-label="Plazas">
                  {[2, 3, 4].map((count) => (
                    <button
                      type="button"
                      key={count}
                      className={room.settings.maxPlayers === count ? 'is-active' : ''}
                      disabled={!isHost || busy || count < room.members.length}
                      onClick={() => onSettings({ maxPlayers: count as 2 | 3 | 4 })}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>

              <div className="settings-block">
                <span className="settings-block__label">Visibilidad</span>
                <div className="segmented segmented--wide">
                  <button
                    type="button"
                    className={room.settings.visibility === 'private' ? 'is-active' : ''}
                    disabled={!isHost || busy}
                    onClick={() => onSettings({ visibility: 'private' })}
                  >
                    <Icon name="link" size={14} /> Privada
                  </button>
                  <button
                    type="button"
                    className={room.settings.visibility === 'public' ? 'is-active' : ''}
                    disabled={!isHost || busy}
                    onClick={() => onSettings({ visibility: 'public' })}
                  >
                    <Icon name="globe" size={14} /> Pública
                  </button>
                </div>
              </div>

              {isHost ? (
                <button
                  type="button"
                  className="button button--primary button--full button--large"
                  onClick={() => setBriefingOpen(true)}
                  disabled={room.members.length < 2 || busy || room.members.some((member) => !member.isBot && !member.connected)}
                >
                  <Icon name="play" size={18} /> Abrir arena
                </button>
              ) : (
                <div className="waiting-box">
                  <span className="waiting-dots"><i /><i /><i /></span>
                  <strong>Esperando al anfitrión</strong>
                  <span>La arena se abrirá cuando el anfitrión dé la señal.</span>
                </div>
              )}
              {room.members.length < 2 && (
                <p className="panel-hint"><Icon name="users" size={14} /> Necesitáis al menos 2 jugadores. Puedes añadir un bot.</p>
              )}
              <p className="panel-hint"><Icon name="lock" size={14} /> El servidor validará todos los turnos y tiradas.</p>
            </div>

            <div className="lobby-note">
              <Icon name="spark" size={18} />
              <p>Consejo: el botón <strong>Copiar enlace</strong> funciona también en el móvil. El código sirve para entrar manualmente.</p>
            </div>
          </aside>
        </section>
      </main>

      <Modal
        open={briefingOpen}
        onClose={() => setBriefingOpen(false)}
        title={`Abrir arena · ${room.settings.mode === 'adventure' ? 'Aventura' : 'Clásico'}`}
        subtitle="Estas son las reglas que aplicará el Núcleo. El servidor las hará cumplir."
        footer={
          <>
            <button type="button" className="button button--ghost" onClick={() => setBriefingOpen(false)}>Volver</button>
            <button type="button" className="button button--primary" onClick={confirmStart} disabled={busy}>
              <Icon name="play" size={17} /> Abrir arena
            </button>
          </>
        }
      >
        <div className="briefing">
          <div className="briefing__summary">
            <span className={`mode-orb mode-orb--${room.settings.mode}`}>
              <Icon name={room.settings.mode === 'adventure' ? 'spark' : 'sword'} size={24} />
            </span>
            <div>
              <strong>{room.settings.mode === 'adventure' ? 'Aventura' : 'Clásico'} · {room.members.length} jugadores</strong>
              <p>
                {room.settings.mode === 'adventure' && room.settings.effectsEnabled
                  ? 'Los efectos están activos. Cada casilla especial explicará lo que ocurre.'
                  : 'Sin anomalías. Reglas clásicas, campos estables y bloqueos.'}
              </p>
            </div>
          </div>
          <ul className="briefing__rules">
            <li><span>5</span> Despliega una unidad desde el muelle. El 5 se consume en la salida.</li>
            <li><span>6</span> Repites tirada. Tres seises consecutivos te penalizan.</li>
            <li><span>✧</span> Captura en celdas normales para devolver rivales al muelle y repetir tirada.</li>
            <li><span>◎</span> Necesitas la tirada exacta para entrar en el Núcleo.</li>
            {room.settings.mode === 'adventure' && room.settings.effectsEnabled && (
              <li><span>?</span> Portales, cofres y fisuras se aplican solo con la tirada; no encadenan anomalías.</li>
            )}
          </ul>
          <p className="briefing__note">
            Al abrir la arena se asignan los escuadrones. Puedes consultar el manual completo durante la partida desde «Reglas».
          </p>
        </div>
      </Modal>
    </div>
  );
}
